import { useEffect, useState } from 'hono/jsx';
import { Layout } from '../components/Layout';
import { BackLink } from '../components/BackLink';
import { ButtonSpinner } from '../components/Spinner';
import { api } from '../lib/api';
import { showToast } from '../lib/toast';
import { navigate } from '../lib/router';
import { useAuth } from '../lib/auth';

// Read MAIN_DOMAIN from the hostname mapping the same way the API does.
function mainDomain(): string {
  const h = window.location.hostname;
  if (h.endsWith('webforcheap.com')) return 'webforcheap.com';
  return 'appsforcheap.com';
}

export function SiteNew() {
  const [name, setName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [email, setEmail] = useState('');
  const [emailEdited, setEmailEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!emailEdited && user?.email) setEmail(user.email);
  }, [user?.email]);

  function onNameInput(v: string) {
    setName(v);
    const auto = v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').replace(/--+/g, '-').substring(0, 50);
    setSubdomain(auto);
  }

  async function onSubmit(e: Event) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api('/sites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, subdomain, email }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error((data as { error?: string }).error || 'Failed to create site');
      const id = (data as { id?: string; site?: { id?: string } }).id || (data as any).site?.id;
      showToast('success', 'Site created', 'Your new site is ready');
      navigate(id ? `/sites/${id}` : '/sites');
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Failed to create site');
      setBusy(false);
    }
  }

  return (
    <Layout active="sites">
      <BackLink to="/sites" label="Back to sites" />
      <div class="max-w-xl">
        <h1 class="text-2xl font-bold text-slate-100 mb-1">Create a new site</h1>
        <p class="text-slate-400 mb-6">Set up the basics. You can build the site itself in the next step.</p>
        <form onSubmit={onSubmit} class="bg-dark-800 border border-slate-700/50 rounded-xl p-6 space-y-5">
          <div>
            <label for="siteName" class="block text-sm font-medium text-slate-300 mb-1.5">Site Name</label>
            <input
              id="siteName" type="text" required maxLength={100} placeholder="My Business" autoFocus
              value={name}
              onInput={(e: Event) => onNameInput((e.currentTarget as HTMLInputElement).value)}
              class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50"
            />
            <p class="text-xs text-slate-500 mt-1.5">Display name for your site</p>
          </div>
          <div>
            <label for="siteSubdomain" class="block text-sm font-medium text-slate-300 mb-1.5">Subdomain</label>
            <div class="flex">
              <input
                id="siteSubdomain" type="text" required minLength={3} maxLength={50} placeholder="mybusiness"
                pattern="[a-z][-a-z0-9]*[a-z0-9]"
                value={subdomain}
                onInput={(e: Event) => setSubdomain((e.currentTarget as HTMLInputElement).value)}
                class="flex-1 px-3 py-2 bg-dark-700 border border-r-0 border-slate-600/50 rounded-l-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 focus:z-10"
              />
              <span class="inline-flex items-center px-3 py-2 border border-slate-600/50 bg-dark-900 text-slate-500 text-sm rounded-r-lg">.{mainDomain()}</span>
            </div>
            <p class="text-xs text-slate-500 mt-1.5">Lowercase letters, numbers, and hyphens only</p>
          </div>
          <div>
            <label for="siteEmail" class="block text-sm font-medium text-slate-300 mb-1.5">Contact Email</label>
            <input
              id="siteEmail" type="email" required placeholder="contact@mybusiness.com"
              value={email}
              onInput={(e: Event) => { setEmailEdited(true); setEmail((e.currentTarget as HTMLInputElement).value); }}
              class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50"
            />
            <p class="text-xs text-slate-500 mt-1.5">Contact form submissions go here. Defaults to your account email, change it if needed.</p>
          </div>
          <div class="flex items-center justify-end gap-3 pt-4 border-t border-slate-700/50">
            <a
              href="/sites"
              onClick={(e: MouseEvent) => { e.preventDefault(); navigate('/sites'); }}
              class="px-4 py-2 text-sm font-medium text-slate-300 hover:bg-dark-700 rounded-lg transition-colors"
            >Cancel</a>
            <button type="submit" disabled={busy} class="px-4 py-2 text-sm font-medium text-dark-900 bg-cyan-500 hover:bg-cyan-400 rounded-lg transition-colors disabled:opacity-60">
              {busy ? <ButtonSpinner /> : 'Create Site'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
